import { useGame } from "@/context/GameContext";
import { ITEM_ICON, itemName, monsterIcon, skillIcon, SKILL_LABEL } from "@/lib/gameData";

const VIEWPORT_BG = {
  woodcutting: "/generated/viewport_forest.png",
  mining: "/generated/viewport_mine.png",
  fishing: "/generated/viewport_forest.png",
  firemaking: "/generated/viewport_forest.png",
  cooking: "/generated/viewport_mine.png",
  smithing: "/generated/viewport_mine.png",
  fletching: "/generated/viewport_forest.png",
  crafting: "/generated/viewport_marketplace.png",
  herblore: "/generated/viewport_farm.png",
  runecrafting: "/generated/viewport_altar.png",
  farming: "/generated/viewport_farm.png",
  hunter: "/generated/viewport_forest.png",
  agility: "/generated/viewport_forest.png",
  thieving: "/generated/viewport_marketplace.png",
  slayer: "/generated/viewport_dungeon.png",
  construction: "/generated/viewport_house.png",
  combat: "/generated/viewport_dungeon.png",
};

export function Viewport() {
  const { snap, content } = useGame();
  const action = snap?.action;
  const combat = snap?.combat;
  const skill = action?.skill;
  const bgKey = combat ? "combat" : skill;
  const bg = VIEWPORT_BG[bgKey] || "/generated/viewport_forest.png";

  const actionDef = action ? content?.actions?.[action.id] : null;
  const progressPct = actionDef ? Math.min(100, Math.round(((action?.progress || 0) / actionDef.duration_ticks) * 100)) : 0;

  const monsterDef = combat && content?.monsters?.[combat.monster_id];

  return (
    <div
      className="rounded panel overflow-hidden relative h-[280px] md:h-[300px] flex flex-col"
      data-testid="viewport"
      style={{
        backgroundImage: `linear-gradient(180deg, rgba(12,10,9,0.35) 0%, rgba(12,10,9,0.9) 100%), url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 noise-overlay pointer-events-none" />
      <div className="relative flex-1 flex items-center justify-center p-8">
        {!action && !combat && (
          <div className="text-center max-w-md">
            <div className="font-display text-3xl heading-gilded mb-2">You stand at rest.</div>
            <p className="text-stone-300 font-body">
              Choose a discipline from the left. Every 600ms the world takes a tick.
              Progress you make here is saved even when you close the tab.
            </p>
          </div>
        )}

        {action && actionDef && (
          <div className="text-center max-w-md" data-testid="viewport-action">
            <div className="mx-auto w-24 h-24 rounded-full border-2 border-amber-700 bg-stone-950/60 flex items-center justify-center mb-4">
              {skillIcon(action.skill) ? (
                <img src={skillIcon(action.skill)} alt="" className="w-14 h-14 rounded-full" />
              ) : (
                <span className="text-4xl">✦</span>
              )}
            </div>
            <div className="uppercase tracking-[0.3em] font-numeric text-xs text-amber-500 mb-1">
              {SKILL_LABEL[action.skill]}
            </div>
            <div className="font-display text-2xl heading-gilded mb-4">{actionDef.name}</div>

            <div className="w-full max-w-sm mx-auto">
              <div className="xp-bar-outer" style={{ height: 10 }}>
                <div className="xp-bar-fill" style={{ width: `${progressPct}%` }} data-testid="viewport-progress" />
              </div>
              <div className="text-xs mt-2 text-stone-400 font-numeric flex justify-between">
                <span>{action.progress || 0}/{actionDef.duration_ticks} ticks</span>
                <span>+{actionDef.xp} XP per yield</span>
              </div>
              {action.status === "inventory_full" && (
                <div className="text-red-400 text-sm mt-3 font-body">Inventory is full. Deposit or drop items to continue.</div>
              )}
              {action.status === "out_of_materials" && (
                <div className="text-red-400 text-sm mt-3 font-body">Out of materials. Gather or withdraw them from the bank.</div>
              )}
              <div className="text-emerald-400 text-sm mt-2 font-numeric">
                Completed: {action.completions || 0}
              </div>
            </div>
          </div>
        )}

        {combat && monsterDef && (
          <div className="text-center max-w-md" data-testid="viewport-combat">
            <div className="mx-auto w-32 h-32 rounded overflow-hidden border-2 border-red-800 bg-stone-950/60 mb-4">
              {monsterIcon(combat.monster_id) ? (
                <img src={monsterIcon(combat.monster_id)} alt={monsterDef.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl">👹</span>
              )}
            </div>
            <div className="uppercase tracking-[0.3em] font-numeric text-xs text-red-400 mb-1">In combat</div>
            <div className="font-display text-2xl heading-gilded mb-4">{monsterDef.name}</div>
            <div className="max-w-sm mx-auto">
              <div className="text-xs text-red-300 font-numeric mb-1">
                HP {combat.monster_hp} / {monsterDef.hp}
              </div>
              <div className="hp-bar-outer">
                <div className="hp-bar-fill" style={{ width: `${(combat.monster_hp / monsterDef.hp) * 100}%` }} />
              </div>
              <div className="text-emerald-400 text-sm mt-3 font-numeric">
                Kills: {combat.kills || 0}
                {combat.respawn_in > 0 && <span className="text-stone-500 ml-3">Respawn: {combat.respawn_in}t</span>}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Inventory strip inside viewport */}
      <div className="relative border-t border-stone-800/80 bg-stone-950/70 px-4 py-2 flex items-center justify-between">
        <div className="text-xs uppercase tracking-widest text-stone-500 font-numeric">
          {combat ? "In combat" : action ? "In progress" : "Idle"}
        </div>
        <div className="text-xs text-stone-500 font-numeric">
          Backpack {snap?.inventory?.length || 0}/28
        </div>
      </div>
    </div>
  );
}
